import type { EmailCompositionValue, SelectOption } from '@kula-ai/components';
import { WHITESPACE_GLOBAL_REGEX } from '@/constants/regex';

/**
 * Compare on visible text, not markup. Editors re-serialise the HTML they are
 * seeded with — tags get normalised and tokens like `{{survey.link}}` become
 * node views — so a raw string compare reports a freshly applied template as
 * already edited. The trade is that a pure formatting change (bolding a word)
 * does not count as an edit, which is far better than a label that reads
 * "Custom" from the moment a template is picked.
 */
function normalise(html: unknown): string {
  if (typeof html !== 'string') return '';
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(WHITESPACE_GLOBAL_REGEX, ' ')
    .trim();
}

function readTemplateField(
  option: SelectOption | undefined,
  key: 'body' | 'subject'
): string {
  const data = option?.data;
  if (!data || typeof data !== 'object') return '';
  return normalise((data as Record<string, unknown>)[key]);
}

/**
 * The template a picker should open on: the BE-flagged default when the row
 * carries one, else the first option. `/ats_comm_templates` does not document
 * `is_default`, so the fallback is what actually runs today.
 */
export function pickDefaultTemplateOption(
  options: SelectOption[]
): SelectOption | undefined {
  const flagged = options.find((option) => {
    const data = option?.data;
    return (
      !!data &&
      typeof data === 'object' &&
      (data as { is_default?: unknown }).is_default === true
    );
  });
  return flagged ?? options[0];
}

/**
 * Whether the composed subject or body has diverged from the selected
 * template. A content diff, not a dirty flag — reverting an edit restores the
 * template name.
 */
export function isTemplateEdited(
  value: Pick<EmailCompositionValue, 'subject' | 'content'>,
  option: SelectOption | undefined
): boolean {
  if (!option) return false;

  const templateBody = readTemplateField(option, 'body');
  const templateSubject = readTemplateField(option, 'subject');
  const bodyDiverged =
    templateBody !== '' && normalise(value.content) !== templateBody;
  const subjectDiverged =
    templateSubject !== '' && normalise(value.subject) !== templateSubject;

  return bodyDiverged || subjectDiverged;
}
